import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import {
  fetchOrgSettings,
  saveOrgSettings,
  saveErrorDismissed
} from '@/features/org/orgSlice'
import {
  selectOrgSettings,
  selectOrgSettingsCache,
  selectOrgSettingsError,
  selectOrgSaveError,
  selectSavingOrgSettings
} from '@/features/org/orgSelectors'
import type { OrgSettings } from '@/features/org/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

/** The editable org-profile fields, one text input each. */
const FIELDS: readonly { key: keyof OrgSettings; labelKey: string }[] = [
  { key: 'name', labelKey: 'org.name' },
  { key: 'timezone', labelKey: 'org.timezone' },
  { key: 'defaultLanguage', labelKey: 'org.defaultLanguage' },
  { key: 'dateFormat', labelKey: 'org.dateFormat' },
  { key: 'currency', labelKey: 'org.currency' },
  { key: 'industry', labelKey: 'org.industry' },
  { key: 'logo', labelKey: 'org.logo' },
  { key: 'defaultProjectTemplate', labelKey: 'org.defaultProjectTemplate' }
]

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/**
 * The organisation profile form (PX-W6.2, AC-4): loads server values into
 * controlled inputs, tracks dirty state, saves the LOADED editable fields —
 * and on failure keeps the user's edits and shows the mapped message.
 */
export function OrgSettingsForm() {
  const { t } = useTranslation('orgSettings')
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const settings = useAppSelector(selectOrgSettings)
  const cache = useAppSelector(selectOrgSettingsCache)
  const error = useAppSelector(selectOrgSettingsError)
  const saveError = useAppSelector(selectOrgSaveError)
  const saving = useAppSelector(selectSavingOrgSettings)

  // The draft is DERIVED until first edit (null = "show the server values")
  // — no effect-seeded state, so a late-arriving load just appears.
  const [edited, setEdited] = useState<OrgSettings | null>(null)
  const draft: OrgSettings = edited ?? settings ?? {}

  useEffect(() => {
    void dispatch(fetchOrgSettings())
  }, [dispatch])

  const dirty = useMemo(() => {
    if (settings === null || edited === null) return false
    // Manual-pass minor: description rides the SAME gate — a description-only
    // edit is a real edit and must be saveable.
    const fieldsDirty = FIELDS.some(({ key }) => (edited[key] ?? '') !== (settings[key] ?? ''))
    return fieldsDirty || (edited.description ?? '') !== (settings.description ?? '')
  }, [edited, settings])

  const onFieldChanged = (key: keyof OrgSettings, value: string): void => {
    setEdited((current) => ({ ...draft, ...current, [key]: value }))
    dispatch(saveErrorDismissed())
  }

  const onSaveClicked = (): void => {
    // Send the loaded editable fields — a sparse body is not an option the
    // server offers an escape from (it binds the model wholesale).
    void dispatch(saveOrgSettings(draft))
  }

  const saved = !dirty && !saving && saveError === null && settings !== null

  return (
    <section
      aria-labelledby="org-settings-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="org-settings-label" className="flex items-center gap-3">
        <SectionLabel>{t('tabs.organisation')}</SectionLabel>
        {dirty && (
          <span className="text-xs text-muted-foreground">{t('dirtyHint')}</span>
        )}
      </div>
      <div className="mt-3">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/settings" />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={() => void dispatch(fetchOrgSettings())} />
        ) : (
          <>
            <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
              {FIELDS.map(({ key, labelKey }) => (
                <label key={key} className="flex flex-col gap-1 text-sm text-ink-soft">
                  {t(labelKey)}
                  <input
                    type="text"
                    value={(draft[key] as string | undefined) ?? ''}
                    onChange={(event) => onFieldChanged(key, event.target.value)}
                    className={cn(inputClass, 'w-full')}
                  />
                </label>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex flex-col gap-1 text-sm text-ink-soft sm:w-full lg:w-2/3">
                {t('org.description')}
                <textarea
                  rows={3}
                  value={draft.description ?? ''}
                  onChange={(event) => onFieldChanged('description', event.target.value)}
                  className={cn(inputClass, 'w-full')}
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onSaveClicked}
                disabled={saving || !dirty}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {saving ? t('org.saving') : t('org.save')}
              </button>
              {saved && (
                <span role="status" className="text-sm text-done">
                  {t('org.saved')}
                </span>
              )}
              {saveError !== null && (
                <p role="alert" className="text-sm text-blocked">
                  {t('saveFailed')} {toMessage(saveError)}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}
